<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <div class="px-6 py-4 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900">Device List</h2>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-500">
            {{ devices.length }} device{{ devices.length !== 1 ? 's' : '' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="p-6">
      <div class="animate-pulse space-y-4">
        <div v-for="i in 3" :key="i" class="flex items-center space-x-4">
          <div class="w-10 h-10 bg-gray-200 rounded-lg"></div>
          <div class="flex-1">
            <div class="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div class="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="devices.length === 0" class="p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No devices found</h3>
      <p class="mt-1 text-sm text-gray-500">
        No IoT devices are currently registered in the system.
      </p>
    </div>

    <!-- Device List -->
    <div v-else class="divide-y divide-gray-200">
      <div 
        v-for="device in devices" 
        :key="device.deviceId"
        class="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
        @click="$emit('select-device', device)"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <!-- Device Icon -->
            <div 
              class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
              :class="deviceIconClasses(device.status, isDeviceInSync(device))"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>

            <!-- Device Info -->
            <div class="flex-1">
              <div class="flex items-center space-x-2">
                <h3 class="text-sm font-medium text-gray-900">
                  {{ device.deviceId }}
                </h3>
                <span 
                  class="status-indicator flex items-center"
                  :class="statusClasses(device.status)"
                >
                  <span class="pulse-dot mr-1"></span>
                  {{ device.status }}
                  <span v-if="isDeviceInSync(device)" class="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-600 text-xs font-semibold">
                    <svg class="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                    In Sync
                  </span>
                </span>
              </div>
              
              <div class="mt-1 text-xs text-gray-500 space-y-1">
                <div>Type: {{ device.deviceType || 'ESP32' }}</div>
                <div>Last seen: {{ formatLastSeen(device.lastSeen) }}</div>
                <div v-if="device.network?.ipAddress">
                  IP: {{ device.network.ipAddress }}
                </div>
              </div>
            </div>
          </div>

          <!-- Device Actions -->
          <div class="flex items-center space-x-2">
            <button
              @click.stop="$emit('sync-device', device.deviceId)"
              class="p-2 text-blue-400 hover:text-blue-600 rounded-md hover:bg-blue-100"
              title="Sync Device"
            >
              <!-- Cloud Sync Icon -->
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4V2m0 20v-2m8-8h2M2 12H4m15.364-7.364l1.414 1.414M4.222 19.778l1.414-1.414M19.778 19.778l-1.414-1.414M4.222 4.222l1.414 1.414M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </button>
            <button
              @click.stop="$emit('delete-device', device.deviceId)"
              class="p-2 text-red-400 hover:text-red-600 rounded-md hover:bg-red-100"
              title="Delete Device"
            >
              <!-- Trash Icon -->
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <div class="text-right">
              <div class="text-sm font-medium text-gray-900">
                v{{ device.shadow?.version || 0 }}
              </div>
              <div class="text-xs text-gray-500">
                {{ Object.keys(device.shadow?.reported || {}).length }} states
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  devices: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select-device', 'update-desired', 'sync-device', 'delete-device'])


// Deep comparison for sync detection (handles nested objects)
function deepEqual(a, b) {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (typeof a !== 'object' || a === null || b === null) return false
  const aKeys = Object.keys(a)
  for (const key of aKeys) {
    if (!(key in b)) return false
    if (!deepEqual(a[key], b[key])) return false
  }
  return true
}

function isDeviceInSync(device) {
  const reported = device.shadow?.reported || {}
  const desired = device.shadow?.desired || {}
  return deepEqual(desired, reported)
}

const deviceIconClasses = (status, inSync = false) => {
  switch (status) {
    case 'online':
      return 'bg-green-100 text-green-600'        // Green: Connected
    case 'heartbeat_missed':
      return 'bg-yellow-100 text-yellow-600'      // Yellow: Missed heartbeats
    case 'disconnected':
      return 'bg-orange-100 text-orange-600'      // Orange: MQTT connection lost
    case 'offline':
      return 'bg-red-100 text-red-600'            // Red: Offline
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

const statusClasses = (status) => {
  switch (status) {
    case 'online':
      return 'status-online'
    case 'heartbeat_missed':
      return 'status-heartbeat-missed bg-yellow-100 text-yellow-600'
    case 'disconnected':
      return 'status-disconnected bg-orange-100 text-orange-600'
    case 'offline':
      return 'status-offline'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatLastSeen = (lastSeen) => {
  if (!lastSeen) return 'Never'
  
  const now = new Date()
  const seen = new Date(lastSeen)
  const diffMs = now - seen
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  
  if (diffSecs < 60) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}
</script>